import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { useAuth } from '@/hooks/useAuth';
import { LegacyAvatar } from '@/components/ui/LegacyAvatar';
import { useStreamUnreadCount } from '@/lib/useStreamUnreadCount';
import { useApprovalAccess } from '@/hooks/useApprovalAccess';
import { useIsBelowMd } from '@/lib/useMediaQuery';
import { CoBrand } from '@/components/ui/co-brand';
import { isMeridianNewScreensEnabled } from '@/config/featureFlags';
import { renderNavRow, type SidebarNavRowItem } from './sidebar-nav-row';
import { SidebarNavGroup } from './sidebar-nav-group';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
import type { RoleType } from '@/context/AuthContext';
import {
  LayoutDashboard,
  User,
  Clock,
  CalendarDays,
  CalendarClock,
  MessageSquare,
  BarChart3,
  Settings,
  LogOut,
  Crown,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Newspaper,
  Wallet,
  Receipt,
  Send,
  Gift,
  ListChecks,
  X,
} from 'lucide-react';

const client = generateClient();

const menuItems = [
  { name: 'Dashboard', path: '/portal', icon: LayoutDashboard },
  { name: 'Bulletin', path: '/portal/herald', icon: Newspaper },
  { name: 'Calendar', path: '/portal/calendar', icon: CalendarDays },
  // Profile routes to the unified "Your application" surface (/apply) — the
  // legacy ApplicantProfilePage was retired (it read the pre-cutover
  // ApplicantYearProfile roster and stranded approved contestants on the
  // journey card). /apply is now the single contestant profile surface.
  { name: 'Profile', path: '/apply', icon: User },
  // Guidebook retired from the contestant portal (2026-07-29, operator
  // decision) — nav entry, route, page title, and global-search entries all
  // removed. `packages/shared`'s canonical GUIDEBOOK content is untouched
  // (mobile app-store-readiness owns that path, Shared Seam 1).
  { name: 'Service Log', path: '/portal/service-log', icon: Clock },
  { name: 'Podium', path: '/portal/messages', icon: MessageSquare },
  { name: 'Spotlight', path: '/portal/insights', icon: BarChart3 },
  // Documents was relocated from the portal nav into contestant Settings
  // (Settings → Documents), mirroring the admin move. The /portal/documents
  // route is retained for deep links; only the sidebar entry was removed.
];

/**
 * Names of items hoisted to the lower action group (above Settings)
 * rather than rendered in the main nav. Documents used to live here,
 * but it was relocated into contestant Settings as a subpage, so the
 * lower group is currently empty. The partition mechanism is retained
 * for any future utility destination.
 */
const LOWER_GROUP_ITEMS: ReadonlySet<string> = new Set([]);

/**
 * Sub-page declarations per parent product (PR (m.6) Phase 2.6).
 *
 * Each parent product with 2+ sub-pages renders as a
 * `<SidebarNavGroup>` collapsible carrot in the sidebar AND a
 * `<PillTabs>` strip at the top of its parent screen. Labels
 * follow the 12-char rule.
 */
interface SubPageDef extends SidebarNavRowItem {
  tab: string;
}

const VAULT_CONTESTANT_SUBPAGES: ReadonlyArray<SubPageDef> = [
  { name: 'Dashboard', tab: 'dashboard', path: '/portal/vault?tab=dashboard', icon: Receipt },
  { name: 'Submit',    tab: 'submit',    path: '/portal/vault?tab=submit',    icon: Send },
  { name: 'Awards',    tab: 'awards',    path: '/portal/vault?tab=awards',    icon: Gift },
];

const ENVOY_SUBPAGES: ReadonlyArray<SubPageDef> = [
  { name: 'List',     tab: 'list',     path: '/portal/envoy?tab=list',     icon: ListChecks },
  { name: 'Calendar', tab: 'calendar', path: '/portal/envoy?tab=calendar', icon: CalendarClock },
];

/**
 * Names of parent products that render as a SidebarNavGroup
 * (carrot expander) for the contestant portal. Merit is intentionally
 * excluded — judges only see Score Entry (one sub-page = flat row).
 */
const GROUP_PARENT_NAMES: ReadonlySet<string> = new Set(['Vault', 'Envoy']);

/**
 * Flag-gated nav items added by the meridian-redesign spec (Req 13.4,
 * 13.6). These four entries appear in the portal sidebar only when:
 *   - `isMeridianNewScreensEnabled()` returns true, AND
 *   - the current user's role is in the per-item allow-list.
 *
 * Per-item visibility (Requirement 13.6 — mirrors ProtectedRoute gating):
 *   - Merit  → admin or judge
 *   - Envoy  → admin or contestant
 *   - Vault  → admin or contestant
 *
 * Patron is admin-only and lives in `/admin/patron`, so it does NOT
 * appear in the portal sidebar (it is added to AdminSidebar instead).
 *
 * Validates: Requirements 13.4, 13.6, 7.2.
 */
const NEW_SCREEN_NAV_ITEMS: ReadonlyArray<{
  name: string;
  path: string;
  icon: typeof LayoutDashboard;
  allowedRoles: ReadonlyArray<RoleType>;
}> = [
  // Merit moved off the contestant portal in the merit-judging-rebuild spec:
  // judges sign in to a standalone code-gated app (/merit/judge), not the
  // Cognito contestant portal, so there is no portal Merit nav item.
  { name: 'Envoy', path: '/portal/envoy', icon: Megaphone, allowedRoles: ['admin', 'contestant'] },
  { name: 'Vault', path: '/portal/vault', icon: Wallet,    allowedRoles: ['admin', 'contestant'] },
];

export interface SidebarProps {
  /**
   * Whether the mobile drawer is currently open. Below the `md`
   * breakpoint the sidebar is hidden by default and slides in from
   * the left when this prop is `true`. At `md` and above the prop
   * is ignored — the sidebar is always visible.
   */
  mobileOpen?: boolean;
  /**
   * Callback fired when the user closes the mobile drawer (via the
   * X button, backdrop tap, or route change). Owned by the parent
   * `PortalLayout` so it can sync the layout's hamburger state.
   */
  onMobileClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, signOut } = useAuth();
  const [title, setTitle] = useState<string | null>(null);
  const [profilePhotoS3Key, setProfilePhotoS3Key] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('idaho-pageant-sidebar-collapsed');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // PR (k): below the `md` breakpoint (< 768px) the sidebar is now
  // hidden by default and revealed via a hamburger toggle in the
  // mobile header (PR (b2)). When `mobileOpen` is true the sidebar
  // slides in from the left as a 280px-wide drawer with a backdrop.
  // The user's persisted collapse preference (`isCollapsed`) is
  // ignored on mobile because the icon-only collapsed view is no
  // longer the mobile experience — mobile sees either the full
  // drawer (open) or zero nav (closed). At `md` and above the
  // sidebar is always visible and the user's collapse preference
  // applies.
  const isBelowMd = useIsBelowMd();
  const renderCollapsed = !isBelowMd && isCollapsed;

  // Auto-close the mobile drawer on route change so tapping a nav
  // item dismisses it. Skip the close callback if it's not provided
  // (e.g. when the Sidebar is rendered standalone outside the
  // PortalLayout shell).
  useEffect(() => {
    if (isBelowMd && mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // PR (m.6) Phase 2.6.1: single-expanded-group invariant. The
  // manual override is reset whenever the active route changes so
  // the URL-derived rule always wins on navigation. See AdminSidebar
  // for the analogous pattern + comment.
  //
  // contestant-admin-ui-polish 14.9 update: `manuallyOpenGroup` now
  // carries a third meaning via the `'__none__'` sentinel — "user
  // explicitly closed the URL-derived active group". Without this
  // distinction, clicking the expanded parent would set state to
  // null, the URL-derived rule would re-open the same group on the
  // next render (because navigation lands inside that group's
  // prefix), and the user would observe the group flicker open
  // again — the bug reported as #18.
  const NONE_SENTINEL = '__none__';
  const [manuallyOpenGroup, setManuallyOpenGroup] = useState<string | null>(null);
  useEffect(() => {
    setManuallyOpenGroup((prev) => {
      // Plain manual override: clear so URL-derived rule wins.
      // Sentinel "user closed this": preserve as long as we're
      // still inside a group's prefix; reset once we leave.
      if (prev === NONE_SENTINEL) {
        // Mirror the `activeGroupFromPath` prefix matching used in
        // the render closure (defined further down). Inlining it
        // here keeps the effect closure-free of render-only
        // helpers.
        const path = location.pathname;
        const stillInsideGroup =
          path.startsWith('/portal/vault') ||
          path.startsWith('/portal/envoy');
        return stillInsideGroup ? NONE_SENTINEL : null;
      }
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const displayName = user?.fullName || user?.email || 'Contestant';

  // Live unread indicator sourced from Stream Chat's event stream — no
  // polling. The hook returns 0 until Stream is connected (connection is
  // established by MessagesPage on first render).
  const { hasUnread } = useStreamUnreadCount();

  // Approval-gated nav visibility (Podium, Spotlight) derives from
  // the Canonical_Approval_Reader (`useApprovalAccess`), which reads the
  // Approval_SSOT `ApplicationResponse.submissionStatus` — the store the
  // director's approval decision writes (approval-status-ssot-unification
  // Req 4.1). This is the SAME source the /portal/messages route gates on, so
  // the nav items and their routes cannot disagree. The legacy
  // `ApplicantYearProfile.submissionStatus` read (which the approval flow no
  // longer updates) is retired for approval purposes here (Req 4.11).
  const { state: approvalState } = useApprovalAccess();

  // Fetch profilePhotoS3Key from the User table (single source of truth)
  useEffect(() => {
    if (!user?.cognitoSub) return;
    async function fetchUserPhoto() {
      try {
        const userResult = await client.graphql({
          query: `query($cognitoSub: String!) { getUser(cognitoSub: $cognitoSub) { profilePhotoS3Key } }`,
          variables: { cognitoSub: user!.cognitoSub },
          authMode: 'userPool',
        }) as GraphQLResult<{ getUser: { profilePhotoS3Key?: string } | null }>;
        if (userResult.data?.getUser?.profilePhotoS3Key) {
          setProfilePhotoS3Key(userResult.data.getUser.profilePhotoS3Key);
        }
      } catch {}
    }
    fetchUserPhoto();
  }, [user?.cognitoSub]);

  useEffect(() => {
    if (!user?.email) return;
    async function fetchTitle() {
      try {
        const result = await client.graphql({
          query: `query { listApplicants(limit: 200) { items { id email } } }`,
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>;
        const match = result.data?.listApplicants?.items?.find((a: { email: string }) => a.email === user!.email);
        if (!match) return;
        // Title identity read only — the `approvedTitle` display value. This is
        // a permitted non-approval AYP read (Req 9); the `submissionStatus`
        // approval field is intentionally NOT selected here, since approval
        // gating now derives from the Canonical_Approval_Reader (Req 4.11).
        const profileResult = await client.graphql({
          query: `query($applicantId: ID!) { listApplicantYearProfiles(applicantId: $applicantId) { requestedTitle approvedTitle titleApproved competitionYear } }`,
          variables: { applicantId: match.id },
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicantYearProfiles: Array<{ requestedTitle?: string; approvedTitle?: string; titleApproved?: boolean; competitionYear: number }> }>;
        const profile = profileResult.data?.listApplicantYearProfiles?.find((p: { competitionYear: number }) => p.competitionYear === 2026);
        if (profile?.approvedTitle) setTitle(profile.approvedTitle);
        else setTitle('Contestant');
      } catch {}
    }
    fetchTitle();
  }, [user?.email]);

  const handleLogOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <aside
      data-testid="sidebar"
      data-mobile-open={isBelowMd ? mobileOpen : undefined}
      className={`flex-shrink-0 z-40 bg-sidebar border-r border-border flex flex-col h-screen transition-all duration-300 ease-in-out ${
        isBelowMd
          ? `fixed inset-y-0 left-0 w-[280px] shadow-prominent ${
              mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
            }`
          : `relative sticky top-0 ${renderCollapsed ? 'w-20' : 'w-64'}`
      }`}
    >
      {/*
        Mobile close (X) button — visible only below `md`. Lets the
        user dismiss the drawer without backdrop-tapping or
        navigating. Mirrors the mockup at
        `reignara_redesign/app/dashboard/page.tsx`.
      */}
      {isBelowMd && (
        <button
          type="button"
          onClick={() => onMobileClose?.()}
          aria-label="Close navigation"
          data-testid="sidebar-mobile-close"
          className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition-colors bg-transparent border-none cursor-pointer touch-target z-10"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/*
        Collapse / expand button — wrapper makes the click target a
        comfortable 32×32 (the visible pill stays 24×24 inside it).
        `before:` extends an invisible hit-test area another ~6px in
        every direction, since the button is positioned half-off the
        sidebar edge and users would otherwise have to land squarely
        on the chevron icon to trigger it. Without the wrapper the
        collapse worked only when the cursor was inside the icon's
        own 12×12 box, which felt unreliable on a 24×24 visible pill.

        Hidden below `md` because mobile uses the hamburger drawer
        pattern instead of the icon-collapsed rail.
      */}
      <button
        type="button"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        onClick={() => { const next = !isCollapsed; setIsCollapsed(next); try { localStorage.setItem('idaho-pageant-sidebar-collapsed', JSON.stringify(next)); } catch {} }}
        disabled={isBelowMd}
        className={`absolute -right-4 top-[5.5rem] w-8 h-8 flex items-center justify-center z-50 group bg-transparent border-none p-0 before:content-[''] before:absolute before:-inset-1.5 ${isBelowMd ? 'cursor-not-allowed opacity-0 pointer-events-none' : 'cursor-pointer'}`}
      >
        <span className="w-6 h-6 bg-foreground text-background rounded-full flex items-center justify-center shadow-raised group-hover:bg-foreground/80 group-active:scale-95 transition-all">
          {renderCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </span>
      </button>

      <div className="p-8 border-b border-border">
        <div className="flex flex-col items-center gap-3 mb-2">
          <LegacyAvatar
            profilePhotoS3Key={profilePhotoS3Key}
            fullName={displayName}
            size="md"
            variant="contestant"
            shape="rounded"
            className={
              renderCollapsed
                ? '!w-14 !h-14 !min-w-[3.5rem] shrink-0 transition-all duration-300'
                : '!w-20 !h-20 !min-w-[5rem] shrink-0 transition-all duration-300'
            }
          />
          {!renderCollapsed && (
            <div className="min-w-0 text-center">
              <h3 className="font-display text-2xl text-foreground leading-tight truncate">
                {displayName}
              </h3>
              {title ? (
                <span className="text-eyebrow-sm text-primary truncate flex items-center justify-center gap-1 mt-1">
                  <Crown className="w-3 h-3 shrink-0 inline-block" />
                  {title}
                </span>
              ) : (
                <span className="text-eyebrow-sm text-primary truncate flex items-center justify-center gap-1 mt-1">
                  <Crown className="w-3 h-3 shrink-0 inline-block" />
                  Contestant
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Active-tenant switcher (tenant-foundation Task 11) — the Slack-style
          workspace switcher. Quiet with a single Membership, a dropdown with
          more; hidden in the icon-only collapsed rail. */}
      {!renderCollapsed && (
        <div className="px-4 pt-4">
          <TenantSwitcher />
        </div>
      )}

      {(() => {
        // PR (m.6) Phase 2.5: split items into main / lower groups.
        // Main items render in the primary nav stack; lower items
        // (Documents) render below the nav, above Settings/LogOut.
        const allFiltered = menuItems
          .filter((item) => {
            // Approval-gated items (Podium, Spotlight) unlock iff
            // the Canonical_Approval_Reader resolves `full` — i.e. the caller's
            // Approval_SSOT status is APPROVED (subject to the privileged-role
            // rule inside the hook). Loading/error resolve to a non-`full`
            // state, so the item stays hidden until approval is confirmed
            // (Req 4.1, 4.10).
            if (['Podium', 'Spotlight'].includes(item.name)) {
              return approvalState === 'full';
            }
            return true;
          })
          // Append the meridian-redesign flag-gated nav items (Merit /
          // Envoy / Vault). They appear only when the feature flag is
          // on AND the current role is in the item's allow-list (Req
          // 13.4, 13.6). Concatenating after the existing filter keeps
          // every pre-redesign item unchanged in name, path, icon, and
          // order — Property 5 (portal slice) continues to hold.
          .concat(
            isMeridianNewScreensEnabled()
              ? NEW_SCREEN_NAV_ITEMS.filter((item) =>
                  item.allowedRoles.includes(role),
                ).map(({ name, path, icon }) => ({ name, path, icon }))
              : [],
          );
        const mainItems = allFiltered.filter(
          (item) => !LOWER_GROUP_ITEMS.has(item.name),
        );
        const lowerItems = allFiltered.filter((item) =>
          LOWER_GROUP_ITEMS.has(item.name),
        );

        // PR (m.6) Phase 2.6.1: single-expanded-group state. See
        // AdminSidebar for the same pattern.
        function activeGroupFromPath(): string | null {
          for (const name of ['Vault', 'Envoy'] as const) {
            const item = mainItems.find((i) => i.name === name);
            if (item && location.pathname.startsWith(item.path)) return name;
          }
          return null;
        }
        const activeGroupName = activeGroupFromPath();
        const openGroupName =
          manuallyOpenGroup === NONE_SENTINEL
            ? null
            : (manuallyOpenGroup ?? activeGroupName);
        function handleGroupToggle(groupName: string): void {
          setManuallyOpenGroup((prev) => {
            const currentlyOpen =
              prev === NONE_SENTINEL ? null : (prev ?? activeGroupName);
            // Closing the currently-open group sets the sentinel so
            // the URL-derived rule cannot reopen it on the same-prefix
            // navigation that follows the click. Clicking a different
            // group opens that one (closing whichever was previously
            // open).
            return currentlyOpen === groupName ? NONE_SENTINEL : groupName;
          });
        }

        return (
          <>
            <nav className="flex-grow p-4 space-y-2 mt-4 overflow-y-auto">
              {mainItems.map((item) => {
                if (GROUP_PARENT_NAMES.has(item.name)) {
                  // Pick the right sub-page table per product. Both
                  // tables are role-agnostic for the portal sidebar
                  // because contestants only see contestant-flavored
                  // Vault and Envoy is identical for contestant vs
                  // admin (admin sees it via AdminSidebar instead).
                  const subPages =
                    item.name === 'Vault'
                      ? VAULT_CONTESTANT_SUBPAGES
                      : item.name === 'Envoy'
                        ? ENVOY_SUBPAGES
                        : null;
                  if (subPages && subPages.length >= 2) {
                    return (
                      <SidebarNavGroup
                        key={`group-${item.name}`}
                        name={item.name}
                        icon={item.icon}
                        defaultPath={item.path}
                        activePathPrefix={item.path}
                        children={subPages}
                        location={location}
                        renderCollapsed={renderCollapsed}
                        testIdPrefix={`portal-sidebar-${item.name.toLowerCase()}-group`}
                        isOpen={openGroupName === item.name}
                        onToggle={() => handleGroupToggle(item.name)}
                        onNavClick={isBelowMd ? () => onMobileClose?.() : undefined}
                      />
                    );
                  }
                }
                return renderNavRow(item, {
                  location,
                  renderCollapsed,
                  hasUnread,
                  onNavClick: isBelowMd ? () => onMobileClose?.() : undefined,
                });
              })}

              {/* Section break before the utility / account block.
                  PR (d2) — pinned at bottom on desktop (mt-auto in the
                  flex column), scrollable with the nav on mobile. The
                  nav element uses flex-grow + overflow-y-auto on mobile
                  but on desktop the utility group sits outside the
                  scroll area via the md:mt-auto + md:sticky pattern. */}
            </nav>
            <div className="p-4 pt-4 mt-auto border-t border-border space-y-2 shrink-0 md:block">
                {lowerItems.map((item) =>
                  renderNavRow(item, {
                    location,
                    renderCollapsed,
                    hasUnread,
                    tone: 'muted',
                    onNavClick: isBelowMd ? () => onMobileClose?.() : undefined,
                  }),
                )}
                <Link
                  to="/portal/settings"
                  onClick={() => { if (isBelowMd) onMobileClose?.(); }}
                  className="flex items-center gap-4 px-4 py-3 min-h-[var(--touch-target-min)] text-muted-foreground hover:text-foreground transition-colors text-nav-item group relative"
                >
                  <Settings className="w-4 h-4 shrink-0" />
                  {!renderCollapsed && <span>Settings</span>}
                  {renderCollapsed && (
                    <div className="absolute left-full ml-4 px-3 py-2 bg-foreground text-background text-nav-item rounded-sm opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-prominent border border-border">
                      Settings
                    </div>
                  )}
                </Link>
                <button
                  onClick={handleLogOut}
                  className="flex items-center gap-4 px-4 py-3 min-h-[var(--touch-target-min)] text-destructive hover:text-destructive/80 transition-colors text-nav-item w-full bg-transparent border-none cursor-pointer group relative"
                >
                  <LogOut className="w-4 h-4 shrink-0" />
                  {!renderCollapsed && <span>Log Out</span>}
                  {renderCollapsed && (
                    <div className="absolute left-full ml-4 px-3 py-2 bg-foreground text-background text-nav-item rounded-sm opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-prominent border border-border">
                      Log Out
                    </div>
                  )}
                </button>
              </div>
          </>
        );
      })()}

      {/*
        Sidebar-footer slot — co-branding (Req 7.8, 15.3).
        `mt-auto` is a no-op here because the nav above already carries
        `flex-grow`, but it's left in place to keep the footer anchored
        even if a future change removes the nav's flex-grow.
        When the sidebar is collapsed (w-20), the watermark wordmark is
        too wide to render legibly, so it's hidden.
        PR (m.6) Phase 2 — switched from stacked CoBrand to the
        reignara-only watermark variant for a more subtle footer.
        Theme toggle removed in PR (m.0) — dark mode dropped.
      */}
      <div className="mt-auto p-4 border-t border-border flex flex-col items-center gap-3">
        {!renderCollapsed && <CoBrand variant="watermark" />}
      </div>
    </aside>
  );
}
